import {
  AmountCellTwo,
  ApproverCell as SharedApproverCell,
  DebitBank,
  RequestedDate,
  Rereference,
} from "@/app/components/common/table/KendoCustomCells";
import KendoTable, {
  type ColumnConfig,
} from "@/app/components/common/table/KendoTable";
import {
  Checkbox,
  type CheckboxChangeEvent,
} from "@progress/kendo-react-inputs";
import {
  type GridCellProps,
  type GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { useT } from "@/app/hooks/useT";
import { useAppRoutingType } from "@/app/hooks/useAppRoutingType";
import { Constants } from "@/app/utils/AppConstants";
import { getDisplayValue, getInitials } from "@/app/utils/helpers/textHelpers";
import "../../../Dashboard/Dashboard.scss";
import type { ApprovalCardItem } from "../../types/approval";

const AccountNameCell = (props: GridCellProps) => {
  const accountName = props?.dataItem?.accountName;
  const category = props?.dataItem?.paymentCategory;

  return (
    <td className="gx-cell-name">
      <div className="gx-cell-name-content">
        <div className="gx-cell-name-avatar">
          <span className="gx-p12-b">{getInitials(accountName)}</span>
        </div>
        <div className="gx-cell-name-info">
          <div title={accountName}>
            <span className="gx-h6 line-clamp-1 gx-p12-b">
              {getDisplayValue(accountName)}
            </span>
          </div>
          {category && (
            <p className="gx-cell-payee-text-dataitem gx-p10-m">
              {`Category : ${category}`}
            </p>
          )}
        </div>
      </div>
    </td>
  );
};

interface ApproveRequiredProp {
  onRowClick?: (event: GridRowClickEvent) => void;
  showAll?: boolean;
  tableList: ApprovalCardItem[];
  selectedTransactionList?: number[];
  setSelectedTransactionList?: React.Dispatch<React.SetStateAction<number[]>>;
  setCheckAll?: React.Dispatch<React.SetStateAction<boolean>>;
  checkAll?: boolean;
}

const DebitBankCell = (props: GridCellProps) => <DebitBank {...props} />;

const DateCell = (props: GridCellProps) => <RequestedDate {...props} />;

const AmountCell = (props: GridCellProps) => <AmountCellTwo {...props} />;

const ReferenceCell = (props: GridCellProps) => <Rereference {...props} />;

const TotalApproverCell = (props: GridCellProps) => (
  <SharedApproverCell
    dataItem={{
      numberOfLevels: props?.dataItem?.noOfApprLevel,
      numberOfApproved: props?.dataItem?.noOfApproved,
    }}
  />
);

const CheckBoxCell = (
  props: GridCellProps,
  selectedTransactionList: number[],
  setSelectedTransaction?: React.Dispatch<React.SetStateAction<number[]>>,
  setCheckAll?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  return (
    <td className="check-box-cell">
      <div className="flex items-center">
        <Checkbox
          className={
            props?.dataItem?.noOfApproved ===
              props?.dataItem?.noOfApprLevel - 1 ||
            props?.dataItem?.statusCode === "4447"
              ? "cursor-not-allowed"
              : ""
          }
          checked={selectedTransactionList?.includes(props?.dataItem?.recordId)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          disabled={
            props?.dataItem?.noOfApproved ===
              props?.dataItem?.noOfApprLevel - 1 ||
            props?.dataItem?.statusCode === "4447"
          }
          onChange={() => {
            if (setSelectedTransaction) {
              if (
                selectedTransactionList?.includes(props?.dataItem?.recordId)
              ) {
                const filterTransaction = selectedTransactionList.filter(
                  (item: number) => item !== props?.dataItem?.recordId,
                );
                setSelectedTransaction(filterTransaction);
              } else {
                setSelectedTransaction((prev: number[]) => [
                  ...prev,
                  props?.dataItem?.recordId,
                ]);
              }
              if (setCheckAll) {
                setCheckAll(false);
              }
            }
          }}
        />
      </div>
    </td>
  );
};

const AlterApprovalRequiredTable = ({
  onRowClick,
  showAll = false,
  tableList,
  selectedTransactionList = [],
  setSelectedTransactionList,
  setCheckAll,
  checkAll = false,
}: ApproveRequiredProp) => {
  const t = useT();
  const { appRoutingType } = useAppRoutingType();

  const columns: ColumnConfig[] =
    showAll === false || showAll === undefined
      ? [
          ...(appRoutingType === Constants.centralBank
            ? []
            : [
                {
                  field: "bankName",
                  title: t("debit_bank"),
                  cell: DebitBankCell,
                },
              ]),
          {
            field: "accountName",
            title: t("beneficiary"),
            cell: AccountNameCell,
          },

          {
            field: "paymentDate",
            title: t("requested_date"),
            cell: DateCell,
          },

          {
            field: "debitAmount",
            title: t("amount"),
            cell: AmountCell,
            headerClassName: "amount-header-center",
          },

          {
            field: "gxChnNo",
            title: t("growx_reference"),
            cell: ReferenceCell,
          },
          {
            field: "",
            title: "",
            cell: TotalApproverCell,
            width: 70,
          },
        ]
      : [
          {
            field: "",
            headerCell: (
              <Checkbox
                className={
                  tableList.every(
                    (item: ApprovalCardItem) =>
                      item?.numberOfApproved === item?.numberOfLevels - 1 ||
                      item?.statusCode === "4447",
                  )
                    ? "gx-font-medium cursor-not-allowed"
                    : "gx-font-medium"
                }
                checked={checkAll}
                disabled={tableList.every(
                  (item: ApprovalCardItem) =>
                    item?.numberOfApproved === item?.numberOfLevels - 1 ||
                    item?.statusCode === "4447",
                )}
                onChange={(event: CheckboxChangeEvent) => {
                  if (setCheckAll) {
                    setCheckAll(event?.value);
                    if (event?.value === false && setSelectedTransactionList) {
                      setSelectedTransactionList([]);
                    } else if (setSelectedTransactionList) {
                      const selected = tableList
                        ?.slice(0, 10)
                        ?.map((item: ApprovalCardItem) => {
                          return item?.recordId ?? item.id;
                        });
                      setSelectedTransactionList(selected);
                    }
                  }
                }}
              />
            ),
            cell: (props) =>
              CheckBoxCell(
                props,
                selectedTransactionList,
                setSelectedTransactionList,
                setCheckAll,
              ),
            width: 60,
          },
          ...(appRoutingType === Constants.centralBank
            ? []
            : [
                {
                  field: "bankName",
                  title: t("debit_bank"),
                  cell: DebitBankCell,
                },
              ]),
          {
            field: "accountName",
            title: t("beneficiary"),
            cell: AccountNameCell,
          },
          {
            field: "paymentDate",
            title: t("requested_date"),
            cell: DateCell,
          },

          {
            field: "debitAmount",
            title: t("amount"),
            cell: AmountCell,
          },
          {
            field: "gxChnNo",
            title: t("growx_reference"),
            cell: ReferenceCell,
          },
          {
            field: "",
            title: "",
            cell: TotalApproverCell,
            width: 70,
          },
        ];

  return (
    <KendoTable
      data={tableList}
      columns={columns}
      onRowClick={onRowClick}
      className="kendo-grid-transactions"
      showHeader={tableList?.length > 0}
    />
  );
};

export default AlterApprovalRequiredTable;
